
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

type AddCommunityMemberDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const COMMUNITY_CATEGORIES = [
  { id: "TEACHING_STAFF", label: "Teaching Staff" },
  { id: "NON_TEACHING_STAFF", label: "Non Teaching Staff" },
  { id: "ADMIN", label: "Admin" }
];

export function AddCommunityMemberDialog({ open, onOpenChange }: AddCommunityMemberDialogProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("TEACHING_STAFF");
  const [contact, setContact] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (!user?.schoolId) {
      toast({
        title: "Error",
        description: "School ID not found",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      console.log("Adding community member with data:", {
        name: name.trim(),
        category: category,
        contact: contact.trim() || null,
        school_id: user.schoolId
      });

      const { error } = await supabase
        .from("community_members")
        .insert([{
          name: name.trim(),
          category: category as "ADMIN" | "NON_TEACHING_STAFF" | "TEACHING_STAFF",
          contact: contact.trim() || null,
          school_id: user.schoolId
        }]);

      if (error) {
        console.error("Detailed error:", error);
        throw error;
      }

      toast({
        title: "Success",
        description: "Community member added successfully",
      });

      // Reset form
      setName("");
      setCategory("TEACHING_STAFF");
      setContact("");
      
      // Close dialog
      onOpenChange(false);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['community-members'] });
      
    } catch (error: any) {
      console.error("Error adding community member:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add community member",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Community Member</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <RadioGroup
              value={category}
              onValueChange={setCategory}
              className="flex flex-col space-y-2"
            >
              {COMMUNITY_CATEGORIES.map((cat) => (
                <div key={cat.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={cat.id} id={cat.id} />
                  <Label htmlFor={cat.id}>{cat.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact">Contact (Optional)</Label>
            <Input
              id="contact"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Enter contact information"
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
